import { Box, Card, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { CfDataGrid } from '../DataGrid/CfDataGrid';
import { CfDataGridComponentContainers } from '../DataGrid/CfDataGridProps';
import { getPagingMode } from '../DataGrid/utility/data-grid-props';
import {
  StyledCardContent,
  StyledCardHeader,
  StyledCardHeaderContainer,
  StyledControlsContainer,
} from './CfDataGridContainer.styles';
import { CfDataGridContainerProps, CfDataGridForStoreDataContainerProps } from './CfDataGridContainerProps';

export function CfDataGridContainer<TItem, TAppState>(
  props: CfDataGridContainerProps<TItem> | CfDataGridForStoreDataContainerProps<TItem, TAppState>
): JSX.Element {
  const [displayMode, setDisplayMode] = useState(props.dataGridProps.displayMode ?? 'table');

  const [containers, setContainers] = useState<CfDataGridComponentContainers>({});

  const containersRef = useRef<CfDataGridComponentContainers>({});

  const showHeader = props.title || props.children || props.customControlsContainer;
  const pagingMode = getPagingMode(props.dataGridProps);

  const headerClassName = props.headerClassName;
  const setContainerRef = useCallback(
    (containerName: keyof CfDataGridComponentContainers, element: HTMLElement | null) => {
      if (containersRef.current[containerName]) {
        return;
      }
      containersRef.current[containerName] = element;
      if (
        (showHeader &&
          containersRef.current.filterInputContainer &&
          containersRef.current.displayModeToggleContainer &&
          containersRef.current.paginationContainer) ||
        containersRef.current.paginationContainer
      ) {
        setContainers(containersRef.current);
      }
    },
    [showHeader]
  );
  const dataGridProps: typeof props.dataGridProps = {
    ...props.dataGridProps,
    displayMode,
    componentContainers: { ...containers, ...props.dataGridProps.componentContainers },
    onDisplayModeChanged: (displayMode) => {
      setDisplayMode(displayMode);
      props.dataGridProps.onDisplayModeChanged?.(displayMode);
    },
  };

  return (
    <>
      <Card
        sx={{
          ...props.sx,
        }}
      >
        <StyledCardContent
          {...(!showHeader && dataGridProps.displayMode === 'card'
            ? { sx: { display: 'none' } }
            : { sx: props.dataGridProps.cardContentSx })}
        >
          {/* HEADER CONTAINER */}
          <StyledCardHeaderContainer className={props.headerClassName} sx={props.headerContainerSx}>
            <StyledCardHeader elevation={0} sx={props.headerSx}>
              {showHeader && (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    pb: dataGridProps.displayMode === 'table' ? 2 : 0,
                    ...props.controlsContainerSx,
                  }}
                >
                  {props.customControlsContainer ? (
                    props.customControlsContainer
                  ) : (
                    <>
                      <Typography data-testid={props.dataGridProps.dataTestId} variant='h3' sx={{ marginTop: -0.75 }}>
                        {props.title}
                      </Typography>
                      <StyledControlsContainer>
                        <Box
                          ref={(elem: HTMLElement | null) => setContainerRef('filterInputContainer', elem)}
                          sx={{ mr: 2 }}
                        />
                        {props.children}
                        <Box ref={(elem: HTMLElement | null) => setContainerRef('displayModeToggleContainer', elem)} />
                      </StyledControlsContainer>
                    </>
                  )}
                </Box>
              )}
            </StyledCardHeader>
          </StyledCardHeaderContainer>

          {/* DISPLAY MODE TABLE GRID */}
          {displayMode === 'table' && (
            <Box>
              <CfDataGrid {...dataGridProps} headerClassName={headerClassName} />
              {props.customFooterContainer && props.customFooterContainer}
            </Box>
          )}
        </StyledCardContent>
      </Card>

      {/* DISPLAY MODE CARD */}
      {displayMode === 'card' && (
        <Box sx={{ position: 'relative' }}>
          <CfDataGrid {...dataGridProps} />
          {props.customFooterContainer}
        </Box>
      )}

      {pagingMode !== 'none' && pagingMode !== 'load-more-scroll' && (
        <Box ref={(elem: HTMLElement | null) => setContainerRef('paginationContainer', elem)} />
      )}
    </>
  );
}
